/** The external-ID mappings search namespaces.
 *
 * Same QuerySearch contract as the other cross-tenant ops grids, over the
 * `demo_ext_id_index` view — a small table (~29k rows), so no mandatory time
 * window and unbounded searches are fine.
 *
 * Two namespaces deserve care:
 *
 *   `value` searches the NORMALISED column for `=` / `=~`, so a leading space
 *   or a case difference cannot hide a row from the operator looking for it.
 *   `=#` (exact) matches the raw column instead, because that is the literal
 *   the matcher compares — 136 production rows carry a leading space.
 *
 *   `used_by_*` filter derived facets. `na` means the tenant does not run that
 *   pipeline at all, so searching `used_by_bookings=no` finds genuinely broken
 *   mappings without sweeping in every import-only operator.
 *
 * Operators per namespace are a SUBSET of the spec's `x-namespace-operators`
 * (ConsoleExtIdFilterNode — contract-tested).
 */
import type { NamespaceConfigMap } from '@/components/QuerySearch';
import { makeSuggestionFetcher } from '@/api/bookings';

import { EXT_ID_TYPES } from '@/api/extIds';

/** Build a local (no-API) suggestion fetcher from a fixed option list. */
function localOptions(options: { value: string; label: string; description?: string }[]) {
  return (query: string) =>
    Promise.resolve(
      options.filter(
        (o) =>
          o.value.toLowerCase().includes(query.toLowerCase()) ||
          o.label.toLowerCase().includes(query.toLowerCase()),
      ),
    );
}

const YES_NO = [
  { value: 'yes', label: 'yes' },
  { value: 'no', label: 'no' },
];

const FACET_STATES = [
  { value: 'yes', label: 'yes', description: 'this pipeline uses the ID' },
  { value: 'no', label: 'no', description: 'the pipeline runs but cannot use this ID' },
  { value: 'na', label: 'na', description: 'the tenant does not run this pipeline' },
];

export function buildExtIdNamespaces(): NamespaceConfigMap {
  return {
    tenant: {
      label: 'tenant',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tenant'),
      help: 'Tour operator (tenant) by code; an all-digits value also matches the tenant id.',
      examples: ['tenant=citywonders'],
    },
    value: {
      label: 'value',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      help: 'The external ID. Matches ignoring case and surrounding whitespace; use `=#` to match the raw stored string exactly (the literal the matcher compares).',
      examples: ['value=~OFF/', 'value=35/6415/EN', 'value=#" MyTrips-18312"'],
    },
    kind: {
      label: 'kind',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localOptions([
        { value: 'primary', label: 'primary', description: "the entity's own custom_id" },
        { value: 'alias', label: 'alias', description: 'an Additional_Ext_ID row' },
      ]),
      help: 'Whether the ID is the entity’s own custom_id or an additional alias row.',
      examples: ['kind=alias'],
    },
    model: {
      label: 'model',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localOptions([
        { value: 'tour', label: 'tour' },
        { value: 'itinerary', label: 'itinerary' },
        { value: 'activity', label: 'activity' },
      ]),
      help: 'Which kind of entity the ID maps to.',
      examples: ['model=itinerary'],
    },
    target: {
      label: 'target',
      operators: ['icontains', 'iexact'],
      valueType: 'string',
      help: 'The mapped entity by name; an all-digits value also matches its id.',
      examples: ['target=~vatican', 'target=4821'],
    },
    id_type: {
      label: 'id_type',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localOptions(EXT_ID_TYPES.map((t) => ({ value: t.value, label: t.label }))),
      help: 'Ops-maintained label for the shape of the ID. Documentation only — matching never reads it.',
      examples: ['id_type=option_id'],
    },
    matching: {
      label: 'matching',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localOptions(YES_NO),
      help: 'Whether the row participates in product → tour matching (alias rows only; a primary ID is always matchable).',
      examples: ['matching=no'],
    },
    queryable: {
      label: 'queryable',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localOptions(YES_NO),
      help: 'Whether the ID may be sent to the vendor as a query argument. Turn it off for matching-only keys such as option IDs and compound forms.',
      examples: ['queryable=no'],
    },
    integration: {
      label: 'integration',
      operators: ['iexact'],
      valueType: 'string',
      help: 'Restech this row is pinned to. `none` finds unscoped rows, which apply to every restech — the overwhelming majority.',
      examples: ['integration=ventrata', 'integration=none'],
    },
    account: {
      label: 'account',
      operators: ['iexact'],
      valueType: 'string',
      help: 'Restech account slug this row is pinned to. `none` finds rows that apply to every account.',
      examples: ['account=banfftours', 'account=none'],
    },
    dangling: {
      label: 'dangling',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localOptions(YES_NO),
      help: 'Alias rows whose target entity no longer exists. Nothing cascades these, so they accumulate silently.',
      examples: ['dangling=yes'],
    },
    used_by_import: {
      label: 'used_by_import',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localOptions(FACET_STATES),
      help: 'Whether the XML importer resolves this ID. It matches the literal string, so almost any shape works.',
      examples: ['used_by_import=no'],
    },
    used_by_bookings: {
      label: 'used_by_bookings',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localOptions(FACET_STATES),
      help: 'Whether booking sync can match this ID. `na` = the tenant runs no booking restech (so its IDs are import-only by design); `no` = the pipeline runs but this shape is never produced.',
      examples: ['used_by_bookings=no', 'used_by_bookings=na'],
    },
    used_by_api: {
      label: 'used_by_api',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localOptions(FACET_STATES),
      help: 'Whether the ID can be sent to the vendor API. Compound keys are matching forms, not product ids.',
      examples: ['used_by_api=no'],
    },
    comment: {
      label: 'comment',
      operators: ['icontains', 'iexact'],
      valueType: 'string',
      help: 'The ops note on the mapping. `none` finds mappings nobody has explained yet.',
      examples: ['comment=~supplier renamed', 'comment=none'],
    },
    has_link: {
      label: 'has_link',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localOptions(YES_NO),
      help: 'Whether the mapping carries an external link to the product or tour page.',
      examples: ['has_link=no'],
    },
    created: {
      label: 'created',
      operators: ['exact', 'range'],
      valueType: 'date',
      help: 'When the mapping row was created.',
      examples: ['created=2026-09-01', 'created=2026-09-01..2026-09-20'],
    },
  };
}
